import React, { useMemo, useState, useEffect } from 'react'
import { useSwipeable } from 'react-swipeable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';
import ScheduleView from '../../ScheduleView';
import { isAndroid } from 'react-device-detect';

export const inlineStylesheetsIntoClone = async (cloneDoc) => {
    const cssTexts = [];
    const inlinedUrls = new Set();
    for (const sheet of Array.from(document.styleSheets)) {
        try {
            cssTexts.push(Array.from(sheet.cssRules).map(rule => rule.cssText).join('\n'));
            if (sheet.href) inlinedUrls.add(sheet.href);
        } catch {
            // Keep unreadable cross-origin sheets: they include the wallpaper font.
        }
    }
    const style = cloneDoc.createElement('style');
    style.textContent = cssTexts.join('\n');
    cloneDoc.head.appendChild(style);

    cloneDoc.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
        if (inlinedUrls.has(link.href)) link.remove();
    });
    // html2canvas measures text after this callback. Keep its font metrics stable.
    if (cloneDoc.fonts) await cloneDoc.fonts.ready;
};

const Wallpaper = ({ screenObj, logButtonClick, json, userSelections, scheduleSelection, personalUrl, personalFullUrl }) => {

    const { windowWidth, windowHeight, devicePixelRatio, deviceWidth, deviceHeight, bottomPadding, optionsBarHeight, tableSideMargins } = screenObj;

    const [currentImage, setCurrentImage] = useState(0);
    const [wallpaperMessage, setWallpaperMessage] = useState('');
    const [translateX, setTranslateX] = useState(0); // Translation state

    const qrCodeSize = 64;
    const qrCodeTopMargin = isAndroid ? 90 : 70;
    const qrCodeSideMargin = isAndroid ? 40 : 10;
    const qrCodeBorderWidth = 2;
    const horizontalCanvasWidth = deviceHeight * devicePixelRatio;
    const horizontalCanvasHeight = deviceWidth * devicePixelRatio;
    const horizontalScheduleWidth = deviceWidth - (tableSideMargins * 2);
    const horizontalScheduleHeight = deviceHeight - optionsBarHeight - bottomPadding;

    const refs = useMemo(() => Array.from({ length: json.length }, () => React.createRef()), [json.length]);
    const wallpaperGeneratingMessage = 'One Moment - Generating Wallpaper...';

    const step = windowWidth * 0.625; // same spacing used when placing images

    const clamp = (v, min, max) => Math.max(min, Math.min(v, max));

    const handlers = useSwipeable({
        onSwipedLeft: () => {
            if (currentImage >= json.length - 1) return;
            const next = clamp(currentImage + 1, 0, json.length - 1);
            setCurrentImage(next);
            setTranslateX(-next * step);
        },
        onSwipedRight: () => {
            const next = clamp(currentImage - 1, 0, json.length - 1);
            setCurrentImage(next);
            setTranslateX(-next * step);
        },
        preventDefaultTouchmoveEvent: true,
        trackMouse: true
    });

    useEffect(() => {
        const s = windowWidth * 0.625;
        setTranslateX(-currentImage * s);
    }, [windowWidth, currentImage]);

    const inlineImages = async (rootEl) => {
        const originals = [];
        const imgs = Array.from(rootEl.querySelectorAll('img'));
        for (const img of imgs) {
            originals.push([img, img.src]);
            const resp = await fetch(img.src, { cache: 'force-cache' });
            const blob = await resp.blob();
            img.src = URL.createObjectURL(blob);
        }
        return () => originals.forEach(([img, src]) => (img.src = src));
    };

    // useEffect(() => {
    //     const setup = async () => {
    //         if (!(window.isSecureContext && 'serviceWorker' in navigator && 'caches' in window)) return;

    //         await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
    //         await navigator.serviceWorker.ready;

    //         // Ensure this page is actually controlled by the SW now
    //         if (!navigator.serviceWorker.controller) {
    //             // Listen once for control and then proceed
    //             await new Promise(resolve => {
    //                 const onChange = () => { navigator.serviceWorker.removeEventListener('controllerchange', onChange); resolve(); };
    //                 navigator.serviceWorker.addEventListener('controllerchange', onChange);
    //             });
    //         }
    //     };
    //     setup();
    // }, []);

    const handleCapture = async (orientation = 'vertical') => {
        const isHorizontal = orientation === 'horizontal';
        logButtonClick(isHorizontal ? 'Download Horizontal Wallpaper' : 'Download Wallpaper', personalFullUrl.trim())
        // await registerServiceWorker(); // Register the service worker

        // const CACHE_NAME = 'wallpaper-cache-v1';
        const destinationImages = document.getElementById('destinationImages');
        destinationImages.innerHTML = '';
        setWallpaperMessage(wallpaperGeneratingMessage);
        setTranslateX(0);
        setCurrentImage(0);


        const waitForSWControl = async (timeoutMs = 1500) => {
            if (!('serviceWorker' in navigator)) return false;

            // Ensure installed/activated
            try { await navigator.serviceWorker.ready; } catch { }

            // If this page is already controlled, stop waiting
            if (navigator.serviceWorker.controller) return true;

            // Otherwise wait briefly for controllerchange, then give up
            return new Promise(resolve => {
                let done = false;
                const finish = v => { if (done) return; done = true; resolve(v); };
                const to = setTimeout(() => finish(false), timeoutMs);

                const onChange = () => {
                    navigator.serviceWorker.removeEventListener('controllerchange', onChange);
                    clearTimeout(to);
                    finish(!!navigator.serviceWorker.controller);
                };
                navigator.serviceWorker.addEventListener('controllerchange', onChange);
            });
        };

        // in handleCapture:
        const swReady = await waitForSWControl();
        const canCache = swReady && window.isSecureContext && 'caches' in window;

        // const canCache = window.isSecureContext && 'serviceWorker' in navigator && 'caches' in window;

        const ensureAssetsReady = async (element) => {
            try { await document.fonts.ready; } catch { }

            // Warm headers used on this screen
            const headerImgs = Array.from(document.querySelectorAll("img[src^='/headers/']"))
                .map(i => i.getAttribute('src'));
            const unique = Array.from(new Set(['/nebula.jpg', ...headerImgs]));
            if ('caches' in window) {
                const c = await caches.open('workbox-precache');
                await Promise.all(unique.map(async (p) => {
                    const origin = window.location.origin;
                    const u = new URL(p, origin).toString();
                    const hit = await c.match(u);
                    if (!hit) {
                        try {
                            const r = await fetch(u, { cache: 'force-cache' });
                            if (r.ok) await c.put(u, r.clone());
                        } catch { }
                    }
                }));
            }

            // Wait for <img> nodes inside the export element
            const imgs = Array.from(element.querySelectorAll('img'));
            await Promise.all(imgs.map(img => img.complete ? Promise.resolve()
                : new Promise(res => { img.onload = img.onerror = res; })));

            // Let layout settle
            await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        };

        for (let date = 0; date < json.length; date++) {
            const element = refs[date].current;
            const horizontalElement = document.getElementById(`export-canvas-horizontal-${date}`);
            const captureElement = isHorizontal ? horizontalElement : element;
            const cacheSuffix = isHorizontal ? 'horizontal-wallpaper' : 'wallpaper';
            if (!captureElement) continue;

            await ensureAssetsReady(captureElement);

            const revertImgs = await inlineImages(captureElement);

            const canvas = await html2canvas(captureElement, {
                scale: 1,
                useCORS: true,
                // allowTaint: true,
                backgroundColor: null,
                // foreignObjectRendering: true, // better fidelity when styles are local
                // scrollX: 0,
                // scrollY: 0
                onclone: inlineStylesheetsIntoClone
            });

            revertImgs();
            let imgBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
            if (!imgBlob) imgBlob = await (await fetch(canvas.toDataURL('image/png'))).blob();

            const imgElement = document.createElement('img');

            imgElement.style.width = `${windowWidth / 2}px`;
            imgElement.style.position = 'absolute';
            imgElement.style.left = `calc(50% + ${date * (windowWidth * .625)}px)`;
            imgElement.style.transform = 'translateX(-50%)';

            if (canCache
                // && 1 === 0
            ) {
                const url = new URL(`/generated-${cacheSuffix}-${json[date].date}.png`, window.location.origin);
                const req = new Request(url.href, { headers: { Accept: 'image/png' } });
                const resp = new Response(imgBlob, {
                    status: 200,
                    headers: { 'Content-Type': 'image/png', 'Cache-Control': 'public, max-age=31536000' }
                });

                const cache = await caches.open('wallpaper-cache-v1');
                await cache.put(req, resp.clone());

                const displayUrl = URL.createObjectURL(imgBlob);
                imgElement.src = displayUrl;

                // now point the <img> to the absolute URL
                // imgElement.src = url.href;
                imgElement.role = 'button';
                imgElement.tabIndex = 0;
                imgElement.addEventListener('click', () => onImageTap(url.href, `generated-${cacheSuffix}-${json[date].date}.png`));
                // const key = `/generated-wallpaper-${json[date].date}.png`; // NOTE leading slash
                // const cache = await caches.open('wallpaper-cache-v1');
                // await cache.put(key, new Response(imgBlob, { headers: { 'Content-Type': 'image/png' } }));
                // imgElement.src = key;
                // imgElement.role = 'button';
                // imgElement.tabIndex = 0;
                // imgElement.addEventListener('click', () => onImageTap(key, `generated-wallpaper-${json[date].date}.png`));
            } else {
                // Fallback (no Cache API)
                imgElement.src = URL.createObjectURL(imgBlob);
                // (Optional) store/revoke later with a ref if you want to avoid leaks
            }

            destinationImages.appendChild(imgElement);
        };
        setWallpaperMessage('Press/hold on each image and save to your photos, then set those as new wallpaper images on your device.');
        // await unregisterServiceWorker(); // Unregister the service worker after use
    };

    const onImageTap = async (path, filename = 'wallpaper.png') => {
        try {
            // Get the cached blob (works with your service worker)
            const resp = await fetch(path, { cache: 'force-cache' });
            const blob = await resp.blob();
            const file = new File([blob], filename, { type: 'image/png' });

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: 'EDC Wallpaper',
                    text: 'Save or share your wallpaper'
                });
            } else {
                // Fallback: open in a new tab so the user can long-press and Save Image
                window.open(path, '_blank', 'noopener,noreferrer');
            }
        } catch (e) {
            // Last-resort fallback
            window.open(path, '_blank', 'noopener,noreferrer');
        }
    };

    const dateHTML = (date, index) => {
        const dateStr = typeof date === 'string' ? date : date.date;
        const tz = typeof date === 'string' ? "America/Los_Angeles" : (date.timezone || "America/Los_Angeles");
        const d = new Date(`${dateStr}T12:00:00Z`); // noon UTC to avoid TZ shift
        const weekday = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: tz })
            .format(d);

        return (
            <>
                <div id="headerDay">{weekday}</div>
                <div id="headerDate">Day {index + 1}</div>
            </>
        );
    };

    return (
        <>
            <div
                style={{
                    zIndex: 1000,
                    width: `${windowWidth}px`,
                    height: `${windowHeight}px`,
                    backgroundColor: '#000'
                }}
            >
                <div className="button-container">
                    <button onClick={handleCapture}
                        disabled={wallpaperMessage === wallpaperGeneratingMessage}
                        className="btn btn-primary">
                        <FontAwesomeIcon icon={faDownload} /> Create Wallpaper
                    </button>
                </div>

                <div id="wallpaperMessage">
                    {wallpaperMessage}
                </div>



                {/* where the wallpaper images to download will go */}
                <div
                    id="destinationImagesContainer"
                    style={{
                        touchAction: 'pan-y',
                        height: `${(windowHeight) / 2}px`
                    }}
                    {...handlers}
                >
                    <div id="destinationImages"
                        style={{
                            width: `${windowWidth}px`,
                            height: `${(windowHeight) / 2}px`,
                            transition: 'transform 0.5s ease',
                            transform: `scale(1) translateX(${translateX}px)`,
                            position: 'absolute'
                        }}

                    >
                    </div>
                </div>

                {wallpaperMessage === '' && <div id="wallpaper-thank-you">
                    <h6>This app is ad-free & made with ❤️ <br />
                        <a target="_blank" rel="noreferrer" href="https://cash.app/skydivingjeff">Love it? Please consider tipping a small thank-you!</a></h6>
                </div>}
            </div>

            {json.map((date, dateIndex) => (

                <div
                    id={`export-canvas-${dateIndex}`}
                    key={date.date}
                    className="export-canvas"
                    ref={refs[dateIndex]}
                    style={{
                        height: `${deviceHeight * devicePixelRatio}px`,
                        width: `${deviceWidth * devicePixelRatio}px`,
                        position: 'fixed',      // ensure it's in the render tree
                        left: 0,
                        top: 0,
                        zIndex: -1,
                        pointerEvents: 'none',
                        backgroundImage: `url('/nebula.jpg')`,
                    }}>

                    <div
                        className="export-date"
                        style={{
                            top: `${qrCodeTopMargin * devicePixelRatio}px`,
                            left: `${qrCodeSideMargin * devicePixelRatio}px`,
                            width: `${qrCodeSize * devicePixelRatio}px`,
                            height: `${qrCodeSize * devicePixelRatio}px`,
                            fontSize: `${devicePixelRatio * 20}px`,
                        }}
                    >
                        {dateHTML(date, dateIndex)}
                    </div>

                    {personalUrl &&
                        <div
                            className="export-qr-code"
                            style={{
                                top: `${qrCodeTopMargin * devicePixelRatio}px`,
                                right: `${qrCodeSideMargin * devicePixelRatio}px`,
                                width: `${(qrCodeSize + qrCodeBorderWidth + qrCodeBorderWidth) * devicePixelRatio}px`,
                                height: `${(qrCodeSize + qrCodeBorderWidth + qrCodeBorderWidth) * devicePixelRatio}px`,
                                borderWidth: `${qrCodeBorderWidth * devicePixelRatio}px`
                            }}
                        >
                            <QRCode
                                value={personalFullUrl.trim()}
                                size={qrCodeSize * devicePixelRatio}
                                bgColor="#ffffff"
                                fgColor="#000000"
                                level="H"
                            />
                        </div>
                    }

                    <div
                        className="export-schedule"
                        style={{
                            transform: `scale(${devicePixelRatio}) rotate(0deg)`,
                            bottom: `${bottomPadding * devicePixelRatio}px`,
                            padding: isAndroid ? `0 ${tableSideMargins}px` : 0,
                            width: `${windowWidth - (tableSideMargins * 2)}px`,
                            height: `${deviceHeight - optionsBarHeight - qrCodeTopMargin - qrCodeSize - (qrCodeBorderWidth * 2) - bottomPadding}px`,
                        }}
                    >
                        <ScheduleView
                            data={json[dateIndex]}
                            width={windowWidth - (tableSideMargins * 2)}
                            height={(deviceHeight - optionsBarHeight - qrCodeTopMargin - qrCodeSize - (qrCodeBorderWidth * 2) - bottomPadding)}
                            scheduleSelection={scheduleSelection}
                            userSelections={userSelections}
                            showGroupSchedules={false}
                        />
                    </div>

                    <div className="export-footer" style={{
                        height: `${bottomPadding * devicePixelRatio}px`,
                        width: `${windowWidth * devicePixelRatio}px`,
                        fontSize: `${devicePixelRatio * 10}px`,
                    }}>
                        made with ❤️ edc.sunsetskycreative.com
                    </div>
                </div>
            ))}

            {json.map((date, dateIndex) => (

                <div
                    id={`export-canvas-horizontal-${dateIndex}`}
                    key={`${date.date}-horizontal`}
                    className="export-canvas export-canvas-horizontal"
                    style={{
                        height: `${horizontalCanvasHeight}px`,
                        width: `${horizontalCanvasWidth}px`,
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        zIndex: -1,
                        pointerEvents: 'none',
                        overflow: 'hidden',
                        backgroundImage: `url('/nebula.jpg')`,
                    }}>

                    <div
                        className="export-date"
                        style={{
                            top: `${qrCodeTopMargin * devicePixelRatio}px`,
                            left: `${qrCodeSideMargin * devicePixelRatio}px`,
                            width: `${qrCodeSize * devicePixelRatio}px`,
                            height: `${qrCodeSize * devicePixelRatio}px`,
                            fontSize: `${devicePixelRatio * 20}px`,
                        }}
                    >
                        {dateHTML(date, dateIndex)}
                    </div>

                    {personalUrl &&
                        <div
                            className="export-qr-code"
                            style={{
                                top: `${qrCodeTopMargin * devicePixelRatio}px`,
                                right: `${qrCodeSideMargin * devicePixelRatio}px`,
                                width: `${(qrCodeSize + qrCodeBorderWidth + qrCodeBorderWidth) * devicePixelRatio}px`,
                                height: `${(qrCodeSize + qrCodeBorderWidth + qrCodeBorderWidth) * devicePixelRatio}px`,
                                borderWidth: `${qrCodeBorderWidth * devicePixelRatio}px`
                            }}
                        >
                            <QRCode
                                value={personalFullUrl.trim()}
                                size={qrCodeSize * devicePixelRatio}
                                bgColor="#ffffff"
                                fgColor="#000000"
                                level="H"
                            />
                        </div>
                    }

                    <div
                        className="export-schedule export-schedule-horizontal"
                        style={{
                            transform: `scale(${devicePixelRatio}) rotate(90deg) translateX(${qrCodeTopMargin + qrCodeSize + qrCodeBorderWidth + 20}px) translateY(-${deviceHeight}px)`,
                            transformOrigin: '0 0',
                            width: `${horizontalScheduleWidth}px`,
                            height: `${horizontalScheduleHeight - qrCodeTopMargin - qrCodeSize - (qrCodeBorderWidth * 2)}px`,
                        }}
                    >
                        <ScheduleView
                            data={json[dateIndex]}
                            width={horizontalScheduleWidth}
                            height={horizontalScheduleHeight - qrCodeTopMargin - qrCodeSize - (qrCodeBorderWidth * 2)}
                            scheduleSelection={scheduleSelection}
                            userSelections={userSelections}
                            showGroupSchedules={false}
                        />
                    </div>

                    <div className="export-footer" style={{
                        height: `${bottomPadding * devicePixelRatio}px`,
                        width: `${horizontalCanvasWidth}px`,
                        fontSize: `${devicePixelRatio * 10}px`,
                    }}>
                        made with ❤️ edc.sunsetskycreative.com
                    </div>
                </div>
            ))}

        </>
    )
}

export default Wallpaper;
